/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router-dom';
import { Stack } from 'office-ui-fabric-react';
import clsx from 'clsx';
import { AgreementFormsProvider } from './context/agreement-forms';
import { RejectionMessageContext } from './context/statement-rejection';
import useWindowDimensions from './hooks/useWindowDimensions';
import usePrevious from './hooks/usePrevious';
import Contract from './pages/Contract';
import AddContractTerms from './pages/AddContractTerms';
import ReviewContractTerms from './pages/ReviewContractTerms';
import BundleProductList from './pages/BundleProductList';
import ReviewContractTermEdits from './pages/ReviewContractTermEdits';
import ReviewContractAgreements from './pages/ReviewContractAgreements';
import ReviewAgreementEdits from './pages/ReviewAgreementEdits';
import AddContractAgreements from './pages/AddContractAgreements';
import EditContractTerms from './pages/EditContractTerms';
import AddAgreement from './pages/AddAgreement';
import EditAgreement from './pages/EditAgreement';
import FAQ from './pages/FAQ';
import Home from './pages/Home';
import ContractListing from './pages/ContractListing';
import PartnerDetail from './pages/PartnerDetail';
import PartnersListing from './pages/PartnerListing';
import AddProduct from './pages/AddProduct';
import ProductDetails from './pages/ProductDetails';
import EditProduct from './pages/EditProduct';
import ProductListing from './pages/ProductListing';
import Logout from './pages/Logout';
import NotAuthorized from './pages/NotAuthorized';
import TransactionListing from './pages/TransactionListing';
import StatementListing from './pages/StatementListing';
import DimensionListView from './pages/DimensionListView';
import AddDimensionList from './pages/AddDimensionList';
import AdjustmentListing from './pages/AdjustmentListing';
import AddAdjustment from './pages/AddAdjustment';
import AdjustmentDetails from './pages/AdjustmentDetails';
import AddAdvanceGroup from './pages/AddAdvanceGroup';
import AdvanceGroupDetails from './pages/AdvanceGroupDetails';
import AddDimensionListSearch from './pages/AddDimensionListSearch';
import EditDimensionList from './pages/EditDimensionList';
import Statement from './pages/Statement';
import AddBundle from './pages/AddBundle';
import BundleDetails from './pages/BundleDetails';
import DimensionList from './pages/DimensionList';
import NavigationBar from './components/NavigationBar';
import LoginTimeout from './components/LoginTimeout';
import FlashMessage from './components/FlashMessage';
import Protected from './components/Protected';
import LeftNav from './components/LeftNav';
import CommandBar from './components/CommandBar';
import ScrollToTop from './components/ScrollToTop';
import { isMobileApp } from './utils';
import { getUser, getRemainingLoginTime, refreshAccessToken } from './utils/auth';
import ErrorBoundary from './ErrorBoundary';
import { startIdleTimer } from './utils/IdleTimer';
import { breakpoints } from './theme.style';
import styles from './App.style';
import './App.css';
import AddScopeFilters from './pages/AddScopeFilters';
import AddAdvanceGroupsToContract from './pages/AddAdvanceGroupsToContract';
import AdjustmentForm from './components/AdjustmentForm';
import AdvanceGroupForm from './components/AdvanceGroupForm';
import DriHome from './pages/DriHome';
import PermissionsHome from './pages/PermissionsHome';
import CreateEditUser from './pages/CreateEditUser/CreateEditUser';
import CreateEditRole from './pages/CreateEditRole';
import AddRolePermissions from './pages/AddRolePermissions';
import CalculationFeatures from './pages/CalculationFeatures';
import AddPartner from './pages/AddPartner';

const isMobile = isMobileApp();

const App = () => {
  const { width } = useWindowDimensions();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < breakpoints.lg);
  const [isSmallScreen, setIsSmallScreen] = useState(width <= breakpoints.lg);
  const [showLoginTimeout, setShowLoginTimeout] = useState(false);
  const wasSmallScreen = usePrevious(isSmallScreen);
  const user = getUser();
  if (isSmallScreen && !isCollapsed && !wasSmallScreen) setIsCollapsed(true);

  useEffect(() => {
    setIsSmallScreen(width <= breakpoints.lg);
  }, [width]);

  useEffect(() => {
    if (!isMobile) {
      // Start timer to display X minutes before the token expires to allow for time to countdown.
      startIdleTimer(getRemainingLoginTime(user) - window.config.loginTimeoutWarning, async () => {
        // first attempt to refresh the user's access token so they don't have to login again.
        // if that fails, then show them the login timeout
        try {
          console.log('refreshing users token silently');
          await refreshAccessToken();
          console.log('successfully refreshed the users token silently');
        } catch (error) {
          console.log('refreshing users token silently failed. showing the logout timeout');
          setShowLoginTimeout(true);
        }
      });
    }
  }, [user]);

  return (
    <div role="main" className={styles.root}>
      {showLoginTimeout && <LoginTimeout dismiss={() => setShowLoginTimeout(false)} />}
      {!isMobile && <NavigationBar />}
      {!isMobile && (
        <CommandBar
          menuState={isCollapsed ? 'menu collapsed' : 'menu expanded'}
          toggleLeftNavCollapse={() => setIsCollapsed(!isCollapsed)}
        />
      )}

      <Stack horizontal>
        {!isMobile && <LeftNav isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}

        <Stack
          className={clsx(
            styles.mainContentContainer,
            isMobile && styles.mobileApp,
            isSmallScreen && styles.smallScreen,
          )}
        >
          <ErrorBoundary>
            <Switch>
              <Route exact path="/logout">
                <Logout />
              </Route>

              <Route exact path="/">
                <ScrollToTop />
                <Home />
              </Route>

              <Route exact path="/faq">
                <ScrollToTop />
                <FAQ />
              </Route>

              <Route exact path="/partners">
                <ScrollToTop />
                <FlashMessage />
                <PartnersListing />
              </Route>

              <Route exact path="/contracts">
                <ScrollToTop />
                <FlashMessage />
                <ContractListing />
              </Route>

              <Route
                exact
                path={[
                  '/add-contract',
                  '/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-contract',
                ]}
              >
                <Protected fallback={NotAuthorized} permissions="createContract">
                  <ScrollToTop />
                  <AddContractTerms />
                </Protected>
              </Route>

              <Route
                exact
                path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-contract/scope-filters"
              >
                <Protected fallback={NotAuthorized} permissions="createContract">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddScopeFilters />
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-advancegroup">
                <Protected
                  fallback={NotAuthorized}
                  permissions={['createContract', 'manageAdjustments']}
                >
                  <ScrollToTop />
                  <FlashMessage />
                  <AddAdvanceGroupsToContract />
                </Protected>
              </Route>

              <Route
                exact
                path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/royalty-fees/:agreementAddress(0x[a-fA-F0-9]{40})/edit"
              >
                <Protected fallback={NotAuthorized} permissions="editAgreement">
                  <ScrollToTop />
                  <AgreementFormsProvider>
                    <EditAgreement />
                  </AgreementFormsProvider>
                </Protected>
              </Route>

              <Route
                exact
                path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/royalty-fees/:agreementAddress(0x[a-fA-F0-9]{40})/approve-edits"
              >
                <Protected fallback={NotAuthorized} permissions="approveAgreement">
                  <ScrollToTop />
                  <AgreementFormsProvider>
                    <ReviewAgreementEdits />
                  </AgreementFormsProvider>
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/edit">
                <Protected fallback={NotAuthorized} permissions="editContract">
                  <ScrollToTop />
                  <EditContractTerms />
                </Protected>
              </Route>

              <Route
                exact
                path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-contract/royalty-fees"
              >
                <Protected
                  fallback={NotAuthorized}
                  permissions={['createAgreement', 'submitContractForApproval']}
                >
                  <ScrollToTop />
                  <AgreementFormsProvider>
                    <AddContractAgreements />
                  </AgreementFormsProvider>
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/edit-calc-features">
                <Protected fallback={NotAuthorized} permissions="editContract">
                  <ScrollToTop />
                  <FlashMessage />
                  <CalculationFeatures
                    title="Edit Calculation Features"
                    continueTo="/add-royalty-fee"
                  />
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-calc-features">
                <Protected fallback={NotAuthorized} permissions="editContract">
                  <ScrollToTop />
                  <FlashMessage />
                  <CalculationFeatures
                    title="Add Calculation Features"
                    continueTo="/add-royalty-fee"
                  />
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/add-royalty-fee">
                <Protected fallback={NotAuthorized} permissions="createAgreement">
                  <ScrollToTop />
                  <FlashMessage />
                  <AgreementFormsProvider>
                    <AddAgreement />
                  </AgreementFormsProvider>
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/approve">
                <Protected fallback={NotAuthorized} permissions="approveContract">
                  <ScrollToTop />
                  <ReviewContractTerms />
                </Protected>
              </Route>

              <Route exact path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/approve-edits">
                <Protected fallback={NotAuthorized} permissions="approveContract">
                  <ScrollToTop />
                  <ReviewContractTermEdits />
                </Protected>
              </Route>

              <Route
                exact
                path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})/approve/royalty-fees"
              >
                <Protected fallback={NotAuthorized} permissions="approveAgreement">
                  <ScrollToTop />
                  <AgreementFormsProvider>
                    <ReviewContractAgreements />
                  </AgreementFormsProvider>
                </Protected>
              </Route>

              <Route path="/contracts/:contractAddress(0x[a-fA-F0-9]{40})">
                <ScrollToTop />
                <FlashMessage />
                <Contract />
              </Route>

              <Route path="/partners/:partnerAddress(0x[a-fA-F0-9]{40})">
                <ScrollToTop />
                <PartnerDetail />
              </Route>

              <Route exact path="/partners/add">
                <Protected fallback={NotAuthorized} permissions="createPartner">
                  <ScrollToTop />
                  <AddPartner />
                </Protected>
              </Route>

              <Route path="/bundle" fallback={NotAuthorized} permissions="viewBundles">
                <BundleProductList />
              </Route>

              <Route exact path="/products">
                <ScrollToTop />
                <FlashMessage />
                <ProductListing />
              </Route>

              <Route exact path="/products/add">
                <Protected fallback={NotAuthorized} permissions="createProduct">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddProduct />
                </Protected>
              </Route>

              <Route exact path="/products/:tokenId">
                <ScrollToTop />
                <FlashMessage />
                <ProductDetails />
              </Route>

              <Route
                exact
                path="/products/:tokenId/edit"
                render={routeProps => (
                  <Protected fallback={NotAuthorized} permissions="editProduct">
                    <ScrollToTop />
                    <EditProduct {...routeProps} />
                  </Protected>
                )}
              />

              <Route exact path="/transactions">
                <ScrollToTop />
                <TransactionListing />
              </Route>

              <Route exact path="/statements">
                <ScrollToTop />
                <FlashMessage />
                <StatementListing />
              </Route>

              <Route exact path="/partner/:partnerAddress/statements/:id">
                <ScrollToTop />
                <FlashMessage />
                <RejectionMessageContext>
                  <Statement />
                </RejectionMessageContext>
              </Route>

              <Route exact path="/add-bundle">
                <Protected fallback={NotAuthorized} permissions="manageBundles">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddBundle />
                </Protected>
              </Route>
              <Route exact path="/bundles/:bundleId">
                <Protected fallback={NotAuthorized} permissions="viewBundles">
                  <ScrollToTop />
                  <FlashMessage />
                  <BundleDetails />
                </Protected>
              </Route>
              <Route exact path="/bundles/:bundleId/edit">
                <Protected fallback={NotAuthorized} permissions="manageBundles">
                  <ScrollToTop />
                  <FlashMessage />
                  <BundleDetails />
                </Protected>
              </Route>
              <Route exact path="/adjustments">
                <Protected fallback={NotAuthorized} permissions="viewAdjustments">
                  <ScrollToTop />
                  <AdjustmentListing />
                </Protected>
              </Route>

              <Route exact path="/add-adjustment">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddAdjustment title="Create Adjustment" mode={AdjustmentForm.MODE.CREATE} />
                </Protected>
              </Route>

              <Route exact path="/adjustments/:adjustmentId">
                <Protected fallback={NotAuthorized} permissions="viewAdjustments">
                  <ScrollToTop />
                  <FlashMessage />
                  <AdjustmentDetails />
                </Protected>
              </Route>

              <Route exact path="/adjustments/:adjustmentId/edit">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <AddAdjustment title="Edit Adjustment" mode={AdjustmentForm.MODE.EDIT} />
                </Protected>
              </Route>

              <Route exact path="/adjustments/:adjustmentId/edit-comments">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <AddAdjustment
                    title="Edit Adjustment Comments"
                    mode={AdjustmentForm.MODE.MINOR}
                  />
                </Protected>
              </Route>

              <Route exact path="/adjustments/:adjustmentId/copy">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <AddAdjustment title="Create Adjustment" mode={AdjustmentForm.MODE.COPY} />
                </Protected>
              </Route>

              <Route exact path="/adjustments/:adjustmentId/reverse">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <AddAdjustment title="Reverse Adjustment" mode={AdjustmentForm.MODE.REVERSE} />
                </Protected>
              </Route>

              <Route exact path="/add-advancegroup">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddAdvanceGroup
                    title="Create Advance Group"
                    mode={AdvanceGroupForm.MODE.CREATE}
                  />
                </Protected>
              </Route>

              <Route exact path="/advancegroups/:advancegroupId/edit">
                <Protected fallback={NotAuthorized} permissions="manageAdjustments">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddAdvanceGroup title="Edit Advance Group" mode={AdvanceGroupForm.MODE.EDIT} />
                </Protected>
              </Route>

              <Route exact path="/advancegroups/:advancegroupId">
                <Protected fallback={NotAuthorized} permissions="viewAdjustments">
                  <ScrollToTop />
                  <FlashMessage />
                  <AdvanceGroupDetails />
                </Protected>
              </Route>

              <Route path="/dimensionlists/add/search">
                <Protected fallback={NotAuthorized} permissions="manageDimensionLists">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddDimensionListSearch />
                </Protected>
              </Route>

              <Route path="/dimensionlists/add">
                <Protected fallback={NotAuthorized} permissions="manageDimensionLists">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddDimensionList />
                </Protected>
              </Route>

              <Route path="/dimensionlists/:listId/edit">
                <Protected fallback={NotAuthorized} permissions="manageDimensionLists">
                  <ScrollToTop />
                  <FlashMessage />
                  <EditDimensionList />
                </Protected>
              </Route>

              <Route exact path="/dimensionlists">
                <Protected fallback={NotAuthorized} permissions="viewDimensionLists">
                  <ScrollToTop />
                  <FlashMessage />
                  <DimensionListView />
                </Protected>
              </Route>

              <Route path="/dimensionlists/:listId/:listType">
                <Protected fallback={NotAuthorized} permissions="viewDimensionLists">
                  <ScrollToTop />
                  <FlashMessage />
                  <RejectionMessageContext>
                    <DimensionList />
                  </RejectionMessageContext>
                </Protected>
              </Route>

              <Route path="/driHome">
                <Protected
                  fallback={NotAuthorized}
                  permissions={[
                    'manageDRIOps',
                    'generateStatement',
                    'approveStatement',
                    'createInvoice',
                  ]}
                  orPermissions
                >
                  <ScrollToTop />
                  <FlashMessage />
                  <DriHome />
                </Protected>
              </Route>

              <Route exact path="/permissions/add-role">
                <Protected fallback={NotAuthorized} permissions="manageRoles">
                  <ScrollToTop />
                  <FlashMessage />
                  <CreateEditRole />
                </Protected>
              </Route>

              <Route path="/permissions/roles/:slug">
                <Protected fallback={NotAuthorized} permissions="manageRoles">
                  <ScrollToTop />
                  <FlashMessage />
                  <CreateEditRole />
                </Protected>
              </Route>

              <Route exact path="/permissions/add-permissions">
                <Protected fallback={NotAuthorized} permissions="manageRoles">
                  <ScrollToTop />
                  <FlashMessage />
                  <AddRolePermissions />
                </Protected>
              </Route>

              <Route exact path="/permissions/add-user">
                <Protected fallback={NotAuthorized} permissions="createUser">
                  <ScrollToTop />
                  <FlashMessage />
                  <CreateEditUser />
                </Protected>
              </Route>

              <Route exact path="/permissions/users/:oid">
                <Protected fallback={NotAuthorized} permissions="editUser">
                  <ScrollToTop />
                  <FlashMessage />
                  <CreateEditUser />
                </Protected>
              </Route>

              <Route path="/permissions">
                <Protected fallback={NotAuthorized} permissions="viewRoles">
                  <ScrollToTop />
                  <FlashMessage />
                  <PermissionsHome />
                </Protected>
              </Route>

              <Route>
                <p>404 Not Found</p>
              </Route>
            </Switch>
          </ErrorBoundary>
        </Stack>
      </Stack>
    </div>
  );
};

export default App;
